import React, { useEffect, useState } from 'react';
import Navigation from '../components/Navigation';
import AnimatedBackground from '../components/AnimatedBackground';
import { getAllPosts, BlogPost } from '../utils/markdown';
import { Link } from 'react-router-dom';
import { BookOpen, Sparkles, Calendar, User, Tag, ArrowRight } from 'lucide-react';

export const Blog: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    let isMounted = true;

    const fetchPosts = async () => {
      try {
        console.log('Starting to fetch posts...');
        setLoading(true);
        setError(null);
        
        const allPosts = await getAllPosts();
        console.log('Raw posts data:', allPosts);
        
        if (isMounted) {
          if (!allPosts) {
            console.error('getAllPosts returned null or undefined');
            setError('Failed to load blog posts. Please try again later.');
          } else if (allPosts.length === 0) {
            console.warn('No posts found in getAllPosts response');
            setError('No blog posts found');
          } else {
            console.log('Setting posts state with:', allPosts);
            setPosts(allPosts);
          }
        }
      } catch (error) {
        console.error('Error in fetchPosts:', error);
        if (isMounted) {
          setError('Failed to load blog posts. Please try again later.');
        }
      } finally {
        if (isMounted) {
          console.log('Setting loading to false');
          setLoading(false);
        }
      }
    };

    fetchPosts();

    return () => {
      isMounted = false;
    };
  }, []);

  // Log state changes
  useEffect(() => {
    console.log('Posts state updated:', posts);
  }, [posts]);

  useEffect(() => {
    console.log('Loading state updated:', loading);
  }, [loading]);

  useEffect(() => {
    console.log('Error state updated:', error);
  }, [error]);

  const categories = [
    { id: 'all', label: 'All Posts' },
    { id: 'Cloud Security', label: 'Cloud Security' },
    { id: 'Data Security', label: 'Data Security' },
    { id: 'Identity Security', label: 'Identity Security' },
    { id: 'Database', label: 'Database' },
    { id: 'Design Pattern', label: 'Design Pattern' },
    { id: 'Cloud', label: 'Cloud' },
    { id: 'Distributed Systems', label: 'Distributed Systems' },
    { id: 'Storage', label: 'Storage' },
    { id: 'Performance', label: 'Performance' },
    { id: 'Architecture', label: 'Architecture' },
  ];

  const filteredPosts = activeCategory === 'all'
    ? posts
    : posts.filter(post => post.category === activeCategory);

  console.log('Current state:', {
    posts,
    loading,
    error,
    activeCategory,
    filteredPosts
  });

  return (
    <div className="min-h-screen relative">
      <Navigation />
      <AnimatedBackground />
      
      <main className="pt-24 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="relative mb-12">
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-3xl transform -rotate-1"></div>
            <div className="relative bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="p-2 bg-gradient-to-r from-green-500 to-blue-500 rounded-xl">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-gray-100">
                  Blog
                </h1>
              </div>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto text-center">
                Thoughts, tutorials, and insights about web development and design
              </p>
            </div>
          </div>

          {/* Category Filter */}
          <div className="relative mb-12">
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-blue-500/5 rounded-2xl"></div>
            <div className="relative p-4">
              <div className="flex flex-wrap justify-center gap-3">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`group relative px-6 py-2.5 rounded-xl transition-all duration-300 ${
                      activeCategory === category.id
                        ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white shadow-lg shadow-green-500/25'
                        : 'bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/80 border border-gray-200/50 dark:border-gray-700/50'
                    }`}
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      {category.id === 'all' && <BookOpen className="w-4 h-4" />}
                      {category.id === 'Development' && <Tag className="w-4 h-4" />}
                      {category.id === 'Engineering' && <Tag className="w-4 h-4" />}
                      {category.id === 'Security' && <Sparkles className="w-4 h-4" />}
                      {category.id === 'Career' && <User className="w-4 h-4" />}
                      {category.id === 'Personal' && <Calendar className="w-4 h-4" />}
                      {category.label}
                    </span>
                    {activeCategory === category.id && (
                      <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="text-center py-16">
              <div className="inline-block p-4 bg-red-100 dark:bg-red-900/20 rounded-full mb-4">
                <BookOpen className="w-8 h-8 text-red-500" />
              </div>
              <p className="text-red-600 dark:text-red-400 text-lg">
                {error}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 text-green-600 dark:text-green-400 hover:underline"
              >
                Try again
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && !error && (
            <div className="text-center py-16">
              <div className="inline-block p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4 animate-spin">
                <Sparkles className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                Loading posts...
              </p>
            </div>
          )}

          {/* Blog Posts Grid */}
          {!loading && !error && filteredPosts.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredPosts.map((post, index) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className="group relative bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-lg p-3 border border-gray-200/50 dark:border-gray-700/50 hover:shadow-lg transition-all duration-300"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {/* Card Content */}
                  <div className="relative z-10">
                    {/* Category and Date */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="px-2 py-0.5 bg-green-500/10 dark:bg-green-400/10 text-green-600 dark:text-green-400 rounded-full text-xs font-medium">
                        {post.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                        <Calendar className="w-3 h-3" />
                        {new Date(post.date).toLocaleDateString()}
                      </span>
                    </div>

                    {/* Title */}
                    <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-1.5 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors line-clamp-2">
                      {post.title}
                    </h2>

                    {/* Excerpt */}
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                      {post.excerpt}
                    </p>

                    {/* Meta Information and Tags */}
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        <User className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {post.author}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                        <span className="text-xs font-medium">Read</span>
                        <ArrowRight className="w-3 h-3 transform group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {post.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded-full text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                      {post.tags.length > 2 && (
                        <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded-full text-xs font-medium">
                          +{post.tags.length - 2}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Hover Effects */}
                  <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-blue-500/0 rounded-lg opacity-0 group-hover:opacity-5 transition-opacity duration-500"></div>
                  <div className="absolute -inset-0.5 bg-gradient-to-br from-green-500/20 to-blue-500/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition-all duration-500 -z-10"></div>
                </Link>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && filteredPosts.length === 0 && (
            <div className="text-center py-16">
              <div className="inline-block p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                <BookOpen className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                No posts found in this category.
              </p>
              <button
                onClick={() => setActiveCategory('all')}
                className="mt-4 text-green-600 dark:text-green-400 hover:underline"
              >
                View all posts
              </button>
            </div>
          )}
        </div>
      </main>

      <style>
        {`
          @keyframes gradient-shift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>
    </div>
  );
};
